import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import DatePicker from '../../components/DatePicker';
import {
  FileText,
  Loader,
  Trash2,
  Pencil,
  Printer,
  Plus,
  GripVertical,
  Calendar,
  CalendarX,
  Layers,
  AlertTriangle,
} from 'lucide-react';
import { api } from '../../api/client';
import { calendarDayString, formatCalendarDate } from '../../lib/calendar-date';
import { fmtCurrency } from '../../lib/display-labels';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import {
  LOAD_FAILURE_QUERY_OPTIONS,
  queryFailureError,
  queryLoadFailure,
} from '../../lib/load-failure';
import { UNIT_OPTIONS } from '../../constants/units';


const TYPE_ORDER = ['service', 'labour', 'equipment', 'materials', 'other'] as const;
const TYPE_LABELS: Record<string, string> = {
  service: 'SERVICE',
  labour: 'LABOUR',
  equipment: 'EQUIPMENT',
  materials: 'MATERIALS',
  other: 'OTHER',
};

/**
 * AN ITEM'S STATUS, SHOWN ONLY WHEN IT IS NOT ACTIVE.
 *
 * Every row used to carry a status line under its price, and on an active row
 * that line read "Active" in a raw 400-weight green — remapped by nothing, so
 * about 1.5:1 on the white card in light mode. Two problems, and the second is
 * the one that matters.
 *
 * MEASURED IN PRODUCTION 2026-09-22: all 544 rate-sheet items across all four
 * tenants are `active`. Not "mostly" — every one. So the line was structurally
 * incapable of saying anything else: no data any tenant holds could move it,
 * and it cost one line of vertical space per row on a 172-row book while
 * carrying no information at all. That is the structurally-zero KPI shape
 * CLAUDE.md names, wearing a label instead of a number.
 *
 * It is not deleted, because `status` is a real column the edit form writes and
 * an INACTIVE row is worth flagging loudly. It is now an exception badge: shown
 * only when the row is not active, in the saturated slate the register uses, so
 * a reader scanning 172 priced lines sees the two that are switched off.
 *
 * The band's Items stat counts every row; the badge is what distinguishes them.
 */
/**
 * Every non-active status this column can hold, spelled out. `active` is absent
 * on purpose — it is the case the badge is NOT rendered for, and listing it
 * here would invite someone to render it.
 */
const ITEM_STATUS_LABELS: Record<string, string> = {
  inactive: 'Inactive',
};

const ITEM_STATUS_PILL =
  'mt-0.5 inline-flex w-fit items-center rounded-fi-pill border border-black/20 bg-fi-signal-slate px-2 py-0.5 text-[0.6rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink';

/**
 * THE UNLABELLED NUMBER UNDER EACH ROW WAS THE REVENUE CODE.
 *
 * The sub-line was `[unit, revCode].filter(Boolean).join(' · ')`, so a row read
 * "per job · 10" and, where a unit was missing, "· 40". Measured in production
 * 2026-09-22: 48 of 544 items carry a `revenue_code`, all 48 of them Smith's,
 * and every distinct value is a round two-digit number — 10, 20, 30, 40, 50, 60,
 * 70. A bare "10" beside "per job" reads as a quantity, a tier or a rate, and
 * there is nothing on the row to say it is not. It is an accounting code the
 * office maps to a revenue account.
 *
 * Labelled, never dropped: the 48 rows that carry one carry it for a reason,
 * and the other 496 render nothing rather than an empty separator.
 */
const revenueCodeLabel = (code: string | null | undefined) =>
  code ? `Rev code ${code}` : null;

/**
 * `effectiveDate` / `expiryDate` are CALENDAR DAYS. `new Date('2026-07-14')` is
 * UTC midnight — 18:00 on the 13th in Alberta — so the label read a day early.
 *
 * The screen already disagreed with itself: toInputDate() below slices the
 * first ten characters and is correct, so opening the edit form showed a rate
 * sheet effective the 14th while the label above it said the 13th.
 */
function formatDate(dateStr: string | null | undefined): string {
  return formatCalendarDate(dateStr, { month: 'short', day: 'numeric', year: 'numeric' }) ?? '—';
}

function toInputDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  return calendarDayString(dateStr) ?? dateStr;
}

/** @see fmtCurrency — absence renders '—', not '$0.00'. */
const formatPrice = (amount: number | string | null | undefined) => fmtCurrency(amount);

/**
 * THE BAND, IN ONE PLACE, because this page has four states and every one of
 * them has to wear it.
 *
 * The eyebrow is constant and the title is the sheet's own name. It carries the
 * back arrow (drawn by the band, no visible word) and NO description: a
 * description line has never been measured against the hero circle's clearance.
 *
 * The sheet's own status, its client and its type sit in the body — the first
 * in an identity row directly under the band, the rest in the Details panel
 * where they already were. Not on the band, for the reason the two sibling
 * detail pages give: the only free slots there are a description line and the
 * stat strip, and the circle's clearance was swept against stat TEXT only.
 *
 * `stats` is OMITTED — not zeroed — while the sheet has not arrived.
 */
function RateSheetBand({
  title,
  onBack,
  actions,
  stats,
}: {
  title: string;
  onBack: () => void;
  actions?: React.ReactNode;
  stats?: React.ComponentProps<typeof PageHeaderBand>['stats'];
}) {
  return (
    <PageHeaderBand
      eyebrow="Price book"
      title={title}
      onBack={onBack}
      backLabel="Back to Rate Sheets"
      actions={actions}
      stats={stats}
    />
  );
}

/**
 * A record panel, in the register idiom: one amber 4px left edge on the PANEL,
 * an eyebrow and an `<h2>`, and the rows inside it.
 */
function RecordPanel({
  eyebrow,
  title,
  count,
  children,
}: {
  eyebrow: string;
  title: string;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card">
      {/* A div, not a <header>: inside a <section> the element still maps to
          the banner role in some accessibility trees. */}
      <div className="border-b border-fieldiq-border px-4 py-3">
        <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">{eyebrow}</p>
        <h2 className="mt-1 text-base font-extrabold tracking-tight text-white">
          {title}
          {count !== undefined && <span className="ml-2 font-bold text-fieldiq-muted">{count}</span>}
        </h2>
      </div>
      {children}
    </section>
  );
}

/**
 * THE BODY ACTION SKINS, in THEME-FOLLOWING ink tokens — the opposite of the
 * band's constant white opacities. `text-fieldiq-text` and
 * `text-fieldiq-accent` are both remapped under the light attribute (the accent
 * to amber-700, 5.02 on the white card); the raw shades these replaced are
 * remapped by nothing.
 */
const ACTION_PRIMARY = 'fi-action fi-action--standard fi-action--primary disabled:opacity-50';
const ACTION_SECONDARY =
  'fi-action fi-action--standard rounded-fi-control border border-fieldiq-border bg-fieldiq-card text-fieldiq-text transition-colors hover:text-fieldiq-accent disabled:opacity-50';
const ACTION_DANGER =
  'fi-action fi-action--standard rounded-fi-control border border-fieldiq-danger/40 bg-fieldiq-card text-fieldiq-text transition-colors hover:bg-fieldiq-danger/10 disabled:opacity-50';

interface EditItem {
  id?: string;
  service_name: string;
  item_type: string;
  unit: string;
  price: string;
  revenue_code: string;
  description: string;
  status: string;
}

const inputClass =
  'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-fieldiq-accent transition-colors';

export const RateSheetDetail: React.FC = () => {
  const { rateSheetId } = useParams<{ rateSheetId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editing, setEditing] = useState(false);

  // ─── Edit form state ──────────────────────────────────────────
  const [editName, setEditName] = useState('');
  const [editCustomerId, setEditCustomerId] = useState('');
  const [editRateType, setEditRateType] = useState('book_rate');
  const [editCurrency, setEditCurrency] = useState('CAD');
  const [editStatus, setEditStatus] = useState('active');
  const [editEffective, setEditEffective] = useState('');
  const [editExpiry, setEditExpiry] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editItems, setEditItems] = useState<EditItem[]>([]);

  // ─── Queries ──────────────────────────────────────────────────
  const sheetQuery = useQuery({
    queryKey: ['rate-sheet', rateSheetId],
    queryFn: () => api.rateSheets.get(rateSheetId!).then((r) => r.data.data ?? null),
    enabled: !!rateSheetId,
    // Retry anything worth retrying and NOT a refusal, and keep the error where
    // the screen can find it. Without this a 500 leaves the query `paused` with
    // `error: null`, `data: undefined` and `isLoading: false` — which the old
    // `isError || !sheet` branch read as "Rate sheet not found", so a dropped
    // connection and a 403 both rendered as a fact about the row.
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const sheet = sheetQuery.data;
  const isLoading = sheetQuery.isLoading;
  // Read off the QUERY, not off `error` alone: a query between retries carries
  // its failure in `failureReason` and nowhere else.
  const sheetFailure = queryLoadFailure(sheetQuery);

  const { data: customers = [] } = useQuery({
    queryKey: ['customers'],
    queryFn: () => api.customers.list().then((r) => {
      const d = r.data;
      return Array.isArray(d) ? d : (d?.data ?? d?.customers ?? []);
    }),
    enabled: editing,
  });

  const { data: configData } = useQuery({
    queryKey: ['rate-sheets-config'],
    queryFn: () => api.rateSheets.getConfig().then((r) => r.data.data),
    enabled: editing,
  });
  const itemTypes: { value: string; label: string }[] = configData?.itemTypes || [];
  const unitOptions: { value: string; label: string }[] = configData?.unitOptions || [];
  // What the select actually renders. Resolving the fallback HERE rather than
  // inside the JSX is what lets the blank-unit guard below cover both branches:
  // during the config-pending window even a canonical unit is absent from the
  // list, and a unit that is absent from the list renders blank.
  // UNIT_OPTIONS is the same list the server sends; it stands in while
  // GET /rate-sheets/config is in flight or if it fails.
  const unitChoices = unitOptions.length > 0 ? unitOptions : UNIT_OPTIONS;

  // ─── Populate edit form ───────────────────────────────────────
  function populateForm() {
    if (!sheet) return;
    setEditName(sheet.name || '');
    setEditCustomerId(sheet.customerId || sheet.customer_id || '');
    setEditRateType(sheet.rateType || sheet.rate_type || 'book_rate');
    setEditCurrency(sheet.currency || 'CAD');
    setEditStatus(sheet.status || 'active');
    setEditEffective(toInputDate(sheet.effectiveDate || sheet.effective_date));
    setEditExpiry(toInputDate(sheet.expiryDate || sheet.expiry_date));
    setEditNotes(sheet.notes || '');
    setEditItems(
      (sheet.items || []).map((item: any) => ({
        id: item.id,
        service_name: item.service_name || item.serviceName || '',
        item_type: item.item_type || item.itemType || 'service',
        unit: item.unit || '',
        price: item.price != null ? String(item.price) : '',
        revenue_code: item.revenue_code || item.revenueCode || '',
        description: item.description || '',
        status: item.status || 'active',
      }))
    );
  }

  // ─── Mutations ────────────────────────────────────────────────
  const deleteMutation = useMutation({
    mutationFn: () => api.rateSheets.delete(rateSheetId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rate-sheets'] });
      navigate('/rates', { replace: true });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) => api.rateSheets.update(rateSheetId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rate-sheet', rateSheetId] });
      queryClient.invalidateQueries({ queryKey: ['rate-sheets'] });
      setEditing(false);
    },
  });

  // ─── Auto-enter edit mode from ?edit=true ─────────────────────
  useEffect(() => {
    if (searchParams.get('edit') === 'true' && sheet && !editing) {
      populateForm();
      setEditing(true);
      // Remove the query param so refreshing doesn't re-trigger
      searchParams.delete('edit');
      setSearchParams(searchParams, { replace: true });
    }
  }, [sheet, searchParams]);

  // ─── Edit helpers ─────────────────────────────────────────────
  function startEdit() {
    populateForm();
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
  }

  function handleSave() {
    updateMutation.mutate({
      name: editName,
      customerId: editCustomerId || null,
      rateType: editRateType,
      currency: editCurrency,
      status: editStatus,
      effectiveDate: editEffective || null,
      expiryDate: editExpiry || null,
      notes: editNotes || null,
      items: editItems.map((item) => ({
        ...(item.id ? { id: item.id } : {}),
        service_name: item.service_name,
        item_type: item.item_type,
        unit: item.unit || null,
        price: item.price ? Number(item.price) : null,
        revenue_code: item.revenue_code || null,
        description: item.description || null,
        status: item.status,
      })),
    });
  }

  function updateEditItem(index: number, field: keyof EditItem, value: string) {
    setEditItems((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  }

  function removeEditItem(index: number) {
    setEditItems((prev) => prev.filter((_, i) => i !== index));
  }

  function addEditItem() {
    setEditItems((prev) => [
      ...prev,
      { service_name: '', item_type: 'service', unit: '', price: '', revenue_code: '', description: '', status: 'active' },
    ]);
  }

  // ─── Read-only computed values ────────────────────────────────
  const groupedItems = useMemo(() => {
    if (!sheet?.items) return [];
    const groups: Record<string, any[]> = {};
    for (const item of sheet.items) {
      const type = item.item_type || item.itemType || 'other';
      if (!groups[type]) groups[type] = [];
      groups[type].push(item);
    }
    return TYPE_ORDER
      .filter((t) => groups[t] && groups[t].length > 0)
      .map((t) => ({ type: t, label: TYPE_LABELS[t] || t.toUpperCase(), items: groups[t] }));
  }, [sheet]);

  /**
   * The number of priced lines on this sheet. `sheet.items` is what the page
   * renders below, so this cannot disagree with what is on screen — and it is
   * the TOTAL, not the grouped total: TYPE_ORDER drops any item whose
   * `item_type` is outside the five it lists, so summing the groups would hide
   * exactly the rows nobody has classified.
   */
  const itemCount = sheet?.items?.length ?? 0;
  const isActive = sheet?.status === 'active';
  const customerName = sheet?.customerName || sheet?.customer_name;
  const rateType = sheet?.rateType || sheet?.rate_type;
  const effectiveDate = sheet?.effectiveDate || sheet?.effective_date;
  const expiryDate = sheet?.expiryDate || sheet?.expiry_date;
  const currency = sheet?.currency || 'CAD';
  const notes = sheet?.notes;

  // ─── Three load states, never two ───────────────────────────────────
  //
  // `isError || !sheet` collapsed a REFUSAL, an unreachable server and a row
  // that does not exist into one sentence — "Rate sheet not found" — which
  // names deletion, and is wrong for the first two. The four rate-sheet GETs
  // are deliberately open to field crews, so a refusal here is rare and
  // therefore exactly the kind nobody would recognise from the wrong wording.

  if (sheetFailure) {
    return (
      <div data-testid="rate-sheet-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
        <RateSheetBand title="Rate sheet" onBack={() => navigate('/rates')} />
        <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
          <div className="mx-auto max-w-[1920px]">
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-8">
              <LoadFailureNotice
                error={queryFailureError(sheetQuery)}
                /* A PLURAL NOUN PHRASE: the helper renders "not a statement
                   about your {subject}".

                   NO `roleLabel` AND NO `allowed` HERE, unlike the invoice and
                   quote pages. The four rate-sheet GETs are deliberately open
                   to every role so field crews can read prices — there is no
                   role gate on this route — so a 403 is not a statement about
                   who the caller is, and naming "who IS allowed" would be
                   inventing the one fact the refusal cannot supply. */
                subject="rate sheet details"
              />
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div data-testid="rate-sheet-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
        <RateSheetBand title="Loading…" onBack={() => navigate('/rates')} />
        <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
          <div className="mx-auto flex max-w-[1920px] items-center justify-center gap-2 py-20 text-fieldiq-text">
            <Loader size={20} className="animate-spin" /> Loading rate sheet…
          </div>
        </section>
      </div>
    );
  }

  // The load finished, the server answered, and it had no sheet for this id.
  if (!sheet) {
    return (
      <div data-testid="rate-sheet-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
        <RateSheetBand title="Rate sheet not found" onBack={() => navigate('/rates')} />
        <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
          <div className="mx-auto max-w-[1920px]">
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-8 text-center">
              <AlertTriangle size={32} className="mx-auto mb-3 text-fieldiq-muted" aria-hidden="true" />
              <p className="text-sm font-semibold text-white">There is no rate sheet with this id</p>
              <p className="mx-auto mt-1 max-w-sm text-xs text-fieldiq-muted">
                It may have been deleted, or the link may be wrong.
              </p>
              <button onClick={() => navigate('/rates')} className={`${ACTION_PRIMARY} mt-4 inline-flex`}>
                View all rate sheets
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // EDIT MODE
  // ═══════════════════════════════════════════════════════════════
  if (editing) {
    return (
      <div data-testid="rate-sheet-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
        {/* THE BAND WITH NO STAT STRIP. A sheet being edited has no settled
            figures — the form holds the numbers, not the row — and a strip of
            values the reader is in the middle of changing is a claim about a
            document that does not exist yet. `stats` is optional by design. */}
        <RateSheetBand title="Editing rate sheet" onBack={cancelEdit} />

        <main className="mx-auto max-w-[1920px] space-y-4 px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
          {/* The save/cancel pair sits in the BODY, above the form it governs,
              rather than on the band. Everything drawn on the band has to be
              spelled in constant white opacities, and these two are ordinary
              body controls with ordinary theme-following ink. */}
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button onClick={cancelEdit} disabled={updateMutation.isPending} className={ACTION_SECONDARY}>
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={updateMutation.isPending || !editName.trim()}
              className={ACTION_PRIMARY}
            >
              {updateMutation.isPending && <Loader size={14} className="animate-spin" />}
              {updateMutation.isPending ? 'Saving…' : 'Save Changes'}
            </button>
          </div>

          {/* ─── Edit Details Card ──────────────────────────────────── */}
          <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-4 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText size={16} className="text-fieldiq-accent" />
              <h3 className="text-xs font-bold text-white tracking-wider uppercase">Details</h3>
            </div>

            {updateMutation.isError && (
              <div role="alert" className="rounded-fi-panel border border-fieldiq-danger/40 bg-fieldiq-danger/10 p-3 text-sm text-fieldiq-text">
                {(updateMutation.error as any)?.response?.data?.error || 'Failed to save changes'}
              </div>
            )}

            <div>
              <label className="text-xs text-white block mb-1">Sheet Name</label>
              <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} className={inputClass} />
            </div>

            <div>
              <label className="text-xs text-white block mb-1">Client</label>
              <select value={editCustomerId} onChange={(e) => setEditCustomerId(e.target.value)} className={inputClass}>
                <option value="">All Clients</option>
                {customers.map((c: any) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-white block mb-1">Rate Type</label>
                <select value={editRateType} onChange={(e) => setEditRateType(e.target.value)} className={inputClass}>
                  <option value="book_rate">Book Rate</option>
                  <option value="bid_contract">Bid / Contract</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-white block mb-1">Currency</label>
                <select value={editCurrency} onChange={(e) => setEditCurrency(e.target.value)} className={inputClass}>
                  <option value="CAD">CAD</option>
                  <option value="USD">USD</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-white block mb-1">Status</label>
                <select value={editStatus} onChange={(e) => setEditStatus(e.target.value)} className={inputClass}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-white block mb-1">Effective Date</label>
                <DatePicker value={editEffective} onChange={setEditEffective} />
              </div>
              <div>
                <label className="text-xs text-white block mb-1">Expiry Date</label>
                <DatePicker value={editExpiry} onChange={setEditExpiry} />
              </div>
            </div>

            <div>
              <label className="text-xs text-white block mb-1">Notes</label>
              <textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)} rows={3} className={inputClass} />
            </div>
          </div>

          {/* ─── Line Items Header ─────────────────────────────────── */}
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-bold text-white tracking-wider uppercase">
              Line Items ({editItems.length})
            </h3>
            <button
              onClick={addEditItem}
              className="flex items-center gap-1.5 text-sm font-semibold text-fieldiq-accent transition-colors hover:text-fieldiq-accent/80"
            >
              <Plus size={16} />
              Add Line Item
            </button>
          </div>

          {/* ─── Line Item Cards (Lovable Style) ───────────────────── */}
          {editItems.length === 0 && (
            <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-8 text-center">
              <p className="text-white text-sm">No line items. Tap "+ Add Line Item" to start.</p>
            </div>
          )}

          {editItems.map((item, i) => (
            <div key={i} className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-5 space-y-4">
              {/* Card header: grip + #N + name + status dot + trash */}
              <div className="flex items-center gap-3">
                <GripVertical size={18} className="text-fieldiq-border flex-shrink-0" />
                <span className="text-sm text-white/50 font-medium">#{i + 1}</span>
                <h4 className="text-base font-bold text-white flex-1 truncate">
                  {item.service_name || 'New Item'}
                </h4>
                {/* The signal hues, which are declared once and do not follow
                    the theme — the raw 400-weight green and grey this carried
                    are remapped by nothing. */}
                <span
                  aria-hidden="true"
                  title={item.status === 'active' ? 'Active' : 'Inactive'}
                  className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${item.status === 'active' ? 'bg-fi-signal-green' : 'bg-fi-signal-slate'}`}
                />
                <button onClick={() => removeEditItem(i)} className="flex-shrink-0 text-fieldiq-muted transition-colors hover:text-fieldiq-danger">
                  <Trash2 size={18} />
                </button>
              </div>

              {/* Row 1: Type + Unit dropdowns */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-white/60 block mb-1">Type</label>
                  <select
                    value={item.item_type}
                    onChange={(e) => updateEditItem(i, 'item_type', e.target.value)}
                    className={inputClass}
                  >
                    {itemTypes.length > 0
                      ? itemTypes.map((t) => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))
                      : <>
                          <option value="service">Service</option>
                          <option value="equipment">Equipment</option>
                          <option value="labour">Labour</option>
                          <option value="materials">Materials</option>
                          <option value="other">Other</option>
                        </>
                    }
                  </select>
                </div>
                <div>
                  <label className="text-xs text-white/60 block mb-1">Unit</label>
                  <select
                    value={item.unit}
                    onChange={(e) => updateEditItem(i, 'unit', e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select unit...</option>
                    {/*
                      A stored unit that matches no option leaves the select at
                      selectedIndex -1, which renders EMPTY — the row reads as
                      unset when it is not. 213 of TNT's 273 reachable rate
                      sheet items are in that state, 92 of them spelled "5 day",
                      a two-tier rental term the canonical vocabulary cannot
                      express. Saving an untouched row is safe, but a blank
                      required-looking field invites a selection, and PATCH
                      deletes and re-inserts every item, so the original string
                      is not recoverable. Render it as its own selected option
                      so it shows what is actually stored.
                    */}
                    {item.unit && !unitChoices.some((u) => u.value === item.unit) && (
                      <option value={item.unit}>{item.unit}</option>
                    )}
                    {unitChoices.map((u) => (
                      <option key={u.value} value={u.value}>{u.label}</option>
                    ))}
                  </select>
                  {item.unit && !unitChoices.some((u) => u.value === item.unit) && (
                    <p className="text-[11px] text-fieldiq-accent/80 mt-1">
                      Non-standard unit — leave as-is unless you mean to change it.
                    </p>
                  )}
                </div>
              </div>

              {/* Row 2: Service Name */}
              <div>
                <label className="text-xs text-white/60 block mb-1">Service Name *</label>
                <input
                  type="text"
                  value={item.service_name}
                  onChange={(e) => updateEditItem(i, 'service_name', e.target.value)}
                  placeholder="Enter service name"
                  className={inputClass}
                />
              </div>

              {/* Row 3: Price (with $ and CAD) + Revenue Code */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-white/60 block mb-1">Price *</label>
                  <div className="flex items-center gap-0">
                    <span className="text-sm text-white/50 bg-fieldiq-dark border border-fieldiq-border border-r-0 rounded-l-lg px-2.5 py-2.5">$</span>
                    <input
                      type="number"
                      value={item.price}
                      onChange={(e) => updateEditItem(i, 'price', e.target.value)}
                      step="0.01"
                      placeholder="0.00"
                      className="flex-1 bg-fieldiq-dark border border-fieldiq-border border-l-0 border-r-0 px-2 py-2.5 text-sm text-white focus:outline-none"
                    />
                    <span className="text-sm text-white/50 bg-fieldiq-dark border border-fieldiq-border border-l-0 rounded-r-lg px-2.5 py-2.5">{editCurrency}</span>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-white/60 block mb-1">Revenue Code</label>
                  <input
                    type="text"
                    value={item.revenue_code}
                    onChange={(e) => updateEditItem(i, 'revenue_code', e.target.value)}
                    placeholder="e.g. TNG-001"
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Row 4: Description */}
              <div>
                <label className="text-xs text-white/60 block mb-1">Description</label>
                <input
                  type="text"
                  value={item.description}
                  onChange={(e) => updateEditItem(i, 'description', e.target.value)}
                  placeholder="Optional description"
                  className={inputClass}
                />
              </div>
            </div>
          ))}
        </main>

      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // READ MODE
  // ═══════════════════════════════════════════════════════════════
  return (
    <div data-testid="rate-sheet-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <RateSheetBand
        title={sheet.name || '—'}
        onBack={() => navigate('/rates')}
        /* FOUR FIGURES THE SHEET ITSELF STATES, none recomputed and none a
           second answer to a question the API already answers. The two dates
           come off the row through the calendar-day helper (they are calendar
           days, and `new Date()` on one is UTC midnight — a day early here).
           Items and Types are lengths of arrays this screen has in hand and
           renders below, so they cannot disagree with what is on the page.

           NOT "ACTIVE ITEMS", which was the first draft. All 544 rate-sheet
           items in production are `active`, across all four tenants, so that
           stat is STRUCTURALLY the item count — no data any tenant holds could
           move it. A figure whose range is one value is not a measurement, it
           is the count restated. Types varies 1–5 and answers a question a
           reader of a price book actually has.

           THE THIRD LABEL IS FIVE CHARACTERS ON PURPOSE. The hero circle's edge
           crosses the third stat column between 1024 and 1048 wide and the
           labels swept there run to about 70px. All four were re-measured for
           this page anyway. */
        stats={[
          {
            icon: <Calendar className="size-4" aria-hidden="true" />,
            label: 'Effective',
            sub: 'Prices apply from',
            tone: 'success',
            value: formatDate(effectiveDate),
          },
          {
            icon: <CalendarX className="size-4" aria-hidden="true" />,
            label: 'Expires',
            sub: 'Prices apply until',
            tone: 'accent',
            value: formatDate(expiryDate),
          },
          {
            icon: <FileText className="size-4" aria-hidden="true" />,
            label: 'Items',
            sub: 'Priced lines',
            tone: 'info',
            value: displayValue(itemCount),
          },
          {
            icon: <Layers className="size-4" aria-hidden="true" />,
            label: 'Types',
            sub: 'Groups below',
            tone: 'neutral',
            value: displayValue(groupedItems.length),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
        {/* ─── Identity row — the SHEET's status, and the way in to edit ───
            In the body rather than on the band, the way the two sibling detail
            pages put theirs. This badge is about the SHEET and is always shown;
            the per-ITEM badge below is an exception marker and is not. */}
        <div data-testid="rate-sheet-identity-row" className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span
              data-testid="rate-sheet-status-badge"
              className={`inline-flex w-fit items-center gap-1 whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${
                isActive ? 'bg-fi-signal-green' : 'bg-fi-signal-slate'
              }`}
            >
              {isActive ? 'Active' : 'Inactive'}
            </span>
            <span className="text-sm font-semibold text-white">{customerName || 'All Clients'}</span>
            <span className="text-sm text-fieldiq-text">
              {rateType === 'bid_contract' ? 'Bid / Contract' : 'Standard (Book Rate)'}
            </span>
          </div>
          <button onClick={startEdit} className={ACTION_PRIMARY}>
            <Pencil size={14} />
            Edit
          </button>
        </div>

        {/* ─── Details ─────────────────────────────────────────────── */}
        <RecordPanel eyebrow="How this book is scoped" title="Details">
          <dl className="divide-y divide-fieldiq-border text-sm">
            <div className="flex items-center justify-between px-4 py-2.5">
              <dt className="text-fieldiq-text">Client</dt>
              <dd className="font-medium text-white">{customerName || 'All Clients'}</dd>
            </div>
            <div className="flex items-center justify-between px-4 py-2.5">
              <dt className="text-fieldiq-text">Type</dt>
              <dd className="font-medium text-white">
                {rateType === 'bid_contract' ? 'Bid / Contract' : 'Standard (Book Rate)'}
              </dd>
            </div>
            <div className="flex items-center justify-between px-4 py-2.5">
              <dt className="text-fieldiq-text">Currency</dt>
              <dd className="font-medium text-white">{currency}</dd>
            </div>
            <div className="flex items-center justify-between px-4 py-2.5">
              <dt className="text-fieldiq-text">Effective</dt>
              <dd className="font-medium text-white">{formatDate(effectiveDate)}</dd>
            </div>
            <div className="flex items-center justify-between px-4 py-2.5">
              <dt className="text-fieldiq-text">Expires</dt>
              <dd className="font-medium text-white">{formatDate(expiryDate)}</dd>
            </div>
            {notes && (
              <div className="flex items-start justify-between gap-4 px-4 py-2.5">
                <dt className="shrink-0 text-fieldiq-text">Notes</dt>
                <dd className="max-w-[60%] whitespace-pre-wrap text-right font-medium text-white">{notes}</dd>
              </div>
            )}
          </dl>
        </RecordPanel>

        {/* ─── Grouped Items ───────────────────────────────────────── */}
        {groupedItems.length === 0 && (
          <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-8 text-center">
            <p className="text-sm font-semibold text-white">This rate sheet has no line items</p>
            <p className="mx-auto mt-1 max-w-sm text-xs text-fieldiq-muted">
              Add prices with Edit, or import them from a spreadsheet on the rate sheets page.
            </p>
          </div>
        )}

        {groupedItems.map((group) => (
          <RecordPanel
            key={group.type}
            eyebrow="Priced lines"
            /* THE HEADING IS INK, NOT AMBER. It was a raw 400-weight amber,
               a shade theme.css remaps not at all — about 2.1:1 on the white
               card in light mode, on the one element that tells a reader which
               part of the book they are in. Title case rather than the shouted
               constant; the eyebrow above it carries the small-caps.
               (The retired class is described rather than spelled: the Tailwind
               scanner has no syntax and reads a comment as source, so naming it
               here would keep the dead rule in the bundle.) */
            title={group.label.charAt(0) + group.label.slice(1).toLowerCase()}
            count={group.items.length}
          >
            <div className="divide-y divide-fieldiq-border">
              {group.items.map((item: any, idx: number) => {
                const name = item.service_name || item.serviceName || '—';
                const unit = item.unit;
                const revCode = revenueCodeLabel(item.revenue_code || item.revenueCode);
                const price = item.price;
                const itemStatus = item.status || 'active';

                return (
                  <div key={item.id || idx} className="flex items-center justify-between px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-white">{name}</p>
                      {/* THE UNIT AS STORED, deliberately not humanised. 213 of
                          TNT's rate-sheet items carry a unit the canonical
                          vocabulary cannot express ("5 day", a two-tier rental
                          term), and a renderer that tidied those would show a
                          value the edit form then cannot round-trip. The
                          revenue code is LABELLED rather than joined bare — see
                          revenueCodeLabel above for what it was reading as. */}
                      <p className="mt-0.5 text-xs text-fieldiq-text">
                        {[unit, revCode].filter(Boolean).join(' · ') || '—'}
                      </p>
                    </div>
                    <div className="ml-4 flex shrink-0 flex-col items-end">
                      <p className="text-sm font-bold text-white">{formatPrice(price)}</p>
                      {itemStatus !== 'active' && (
                        /* A VALUE OUTSIDE THE MAP READS `—`, NOT ITSELF. The
                           fallback printed the raw column value, which is the
                           "echo the enum" shape CLAUDE.md files under label maps
                           must be exhaustive: a word the screen could not
                           classify, rendered as though it were a state. */
                        <span data-testid="rate-sheet-item-status" className={ITEM_STATUS_PILL}>
                          {ITEM_STATUS_LABELS[itemStatus] ?? '\u2014'}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </RecordPanel>
        ))}

        {/* ─── Secondary Actions ──────────────────────────────────── */}
        {/*
          ONE PRINT BUTTON, NOT TWO.

          "Print" and "Download PDF" sat side by side and BOTH called
          `window.print()`. There is no rate-sheet PDF endpoint in fieldiq-api —
          `api.rateSheets` has config, list, items, get, create, update, delete
          and the two import calls, and nothing else — so the second button
          named a file the app cannot produce and then did something different.
          The browser's own print dialog is how a reader gets a PDF of this
          page, and the button now says the thing it actually does.
        */}
        <div className="flex flex-wrap items-center gap-2 pt-2">
          <button onClick={() => window.print()} className={ACTION_SECONDARY}>
            <Printer size={15} />
            Print
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            aria-label="Delete rate sheet"
            className={`${ACTION_DANGER} ml-auto`}
          >
            <Trash2 size={15} />
            Delete
          </button>
        </div>
        </div>
      </section>

      {/* ─── Delete Confirmation Modal ─────────────────────────── */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-fieldiq-card rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-lg font-bold text-white mb-2">Delete Rate Sheet</h3>
            <p className="text-sm text-white mb-6">
              Are you sure you want to delete &ldquo;{sheet.name}&rdquo;? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 rounded-fi-control border border-fieldiq-border bg-fieldiq-card py-2.5 text-sm font-medium text-fieldiq-text transition-colors hover:text-fieldiq-accent"
                disabled={deleteMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="flex flex-1 items-center justify-center gap-2 rounded-fi-control border border-black/20 bg-fi-signal-red py-2.5 text-sm font-black text-fi-signal-ink transition-colors disabled:opacity-50"
              >
                {deleteMutation.isPending && (
                  <Loader size={14} className="animate-spin" />
                )}
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
